<template>
  <div class="padded">

    <edit-pane v-if="!hidePanel" ref="cd" :title="form.name"
    :is-edit="editModeState" :prefix="prefix" :show-edit-button="isAdding || profile().login === form.login || isSuper() || isManager('admin|management')"
    :parent-managed="parentManaged"
    :self-editable="!isAdding && profile().login === form.login"
    @save="onSave" @cancel="onCancel" @edit="onEdit" @add="onAdd"
    :show-background="!isAdding"  />

    <el-form ref="contactDetailsForm" label-width="90px">

      <el-tabs type="border-card"
        :active-name="currentTab"
        :before-leave="canLeaveTab"
        @tab-click="tabChanged">
        <el-tab-pane name="contact" label="Contact">

          <el-row>
            <!-- XOL-490/493: `required` stays on the el-form-item — that is
                 only the asterisk, cosmetic. The native `required` came OFF
                 the el-input: assigning currentTab routes through el-tabs'
                 :before-leave (canLeaveTab), which is this SAME
                 checkValidity() check, so a native-invalid Name blocked BOTH
                 the save AND the programmatic return to this tab — a Save
                 button that would silently do nothing. validateContactFields()
                 (save-time only) is the one place Name is now enforced. -->
            <el-form-item label="Name" required class="contact-detail__validated" :error="fieldErrors.name">
              <el-input ref="nameInput" name="name" v-model="form.name" :disabled="disabled" placeholder="Full Name" />
            </el-form-item>

            <el-form-item label="Short Name">
              <el-input name="shortName" v-model="form.shortName" :disabled="disabled" placeholder="Short Name" />
            </el-form-item>

            <el-form-item label="Job Title">
              <el-input name="jobTitle" v-model="form.jobTitle" :disabled="disabled" />
            </el-form-item>

          </el-row>

          <el-row>
            <el-form-item label="Phone">
              <el-input type="phone" name="phone" v-model="form.phone" :disabled="disabled" />
            </el-form-item>
            <!-- XOL-493: `:required` is bound, not a permanent asterisk — the
                 API only requires e-mail on create; an edit of a legacy
                 blank-e-mail contact stays optional (emailRequired computed,
                 Q1). No native `required` here — the format check
                 (type="email" + canLeaveTab()'s checkValidity()) is the only
                 native validity this field carries. -->
            <el-form-item label="E-mail" :required="emailRequired" class="contact-detail__validated" :error="fieldErrors.email">
              <el-input ref="emailInput" type="email" name="email" v-model="form.email" :disabled="disabled || emailLocked"
                :aria-describedby="emailLocked ? 'email-locked-hint' : null" />
              <small v-if="emailLocked" id="email-locked-hint" class="contact-detail__hint">Only a super user can change this e-mail.</small>
            </el-form-item>
          </el-row>

          <!-- XOL-674 (Carlos, 2026-10-25): the alerts are an Alerts section at the bottom of the Contact
               tab's fields: three SwitchInput toggles in thirds (stacked below 768px), each named by its
               kind alone (the heading says "Alerts") with a help tip beside the word. Finance Alerts is
               the third, and exists only where the API reads it (showFinanceAlerts: Tower Company AND a
               finance role); the other two never move when it appears. No Yes / No words here
               (:on-text / :off-text null). `input-id` + `aria-label` give each switch the full accessible
               name ("Upload Alerts"), and HelpTip `in-label` keeps a click on the icon from toggling it. -->
          <section class="remarks-section contact-detail__alerts" role="group" :aria-labelledby="alertIds.heading">
            <h5 class="remarks-heading" :id="alertIds.heading">Alerts</h5>
            <el-row>
              <el-col :xs="24" :sm="8">
                <switch-input :input-id="alertIds.upload" aria-label="Upload Alerts" v-model="form.sendUploadAlerts" :disabled="disabled" :on-text="null" :off-text="null">
                  <template #label><span>Upload <help-tip in-label :content="uploadAlertsHelp" /></span></template>
                </switch-input>
              </el-col>

              <el-col :xs="24" :sm="8">
                <switch-input :input-id="alertIds.approval" aria-label="Approval Alerts" v-model="form.sendApprovalAlerts" :disabled="disabled" :on-text="null" :off-text="null">
                  <template #label><span>Approval <help-tip in-label :content="approvalAlertsHelp" /></span></template>
                </switch-input>
              </el-col>

              <el-col v-if="showFinanceAlerts" :xs="24" :sm="8">
                <switch-input :input-id="alertIds.finance" aria-label="Finance Alerts" v-model="form.sendFinanceAlerts" :disabled="disabled" :on-text="null" :off-text="null">
                  <template #label><span>Finance <help-tip in-label :content="financeAlertsHelp" /></span></template>
                </switch-input>
              </el-col>
            </el-row>
          </section>

          <!-- XOL-602/603/604 (D1/D18): the manager's home for "Send invitation" / "Send reset link".
               Managers have no System Details tab, so the action sits on the tab they have: a plain
               block AFTER the Alerts section (not an el-form-item, so nothing inline can reach it),
               shown in view AND edit mode. It is the saved record's action, so it takes
               `initialForm` and says "Save your changes first" while the form differs. A super's
               is on System Details instead (canSendPasswordLinkManager excludes supers).
               NOT gated on Edit being visible: a Finance-only manager gets the action and no Edit
               button. -->
          <!-- Carlos, 2026-10-19: a manager has no System Details tab, so Change Password is here too,
               side by side with the send action in the same flex row (one per line below 768px) as on
               System Details. Change Password (the same PasswordDialog, `PUT /password/change/{id}`)
               needs a SAVED Login ID and a target who is not a super (canChangePasswordManager). With
               no Login ID it is absent, and the send action beside it gives the reason ("This contact
               has no Login ID. Ask a super user to add one."). A super's screen has both on System
               Details instead, never both homes. -->
          <div v-if="canSendPasswordLinkManager" class="contact-detail__password-link">
            <div class="contact-detail__password-actions">
              <password-dialog v-if="canChangePasswordManager" v-model="form" change :disabled="false" />
              <password-link-action home="contact" :contact="initialForm" :dirty="passwordLinkDirty" />
            </div>
          </div>

          <!-- XOL-392: on /profile the password control lives HERE, on the one
               tab that screen has. Everywhere else it sits on System Details
               beside the Login ID it belongs to. "My account" is not a
               contact-admin screen: there is no Enabled?, no Login ID field and
               no privileges to sit next to, so a tab for one button would be
               chrome for its own sake. Always shown — /profile is by definition
               the self case, which PUT /password/change/{id} permits
               (ChangePasswordRequest::authorize() is self-or-super) and which
               is exempt from the view-only write block (XOL-371). -->
          <div v-if="selfProfile" class="text-right">
            <password-dialog v-model="form" change :disabled="false" />
          </div>

        </el-tab-pane>



        <!-- XOL-392: this tab is the account surface — Enabled?, Login ID and
             the password all live here now, in both create and edit. It is an
             administrator acting ON a contact, so it is suppressed entirely on
             /profile (selfProfile), which has its own password button above.
             Credentials are offered on create only for a super — see
             canStageCredential — because stripSuperOnlyFields() keeps
             `password` while stripping `login`/`enabled` for anyone else. -->
        <el-tab-pane name="systemDetails" v-if="showSystemDetails" label="System Details">

          <!-- XOL-392 (2026-08-20): three blocks, per Carlos.
                 LEFT   - who this account is: Enabled?, Login ID, password.
                 RIGHT  - what it can see: Default View + the roles transfer.
                 BOTTOM - what it may do: the four privilege toggles, full width
                          because each carries an explanatory alert and four of
                          those do not fit in half a row.
               Left and right go full width below `lg`: el-transfer is two
               panels plus a button column and overflows a half column at md. -->
          <el-row :gutter="12">

            <!-- CREATE ONLY: full width above both columns. -->
            <el-col v-if="canStageCredential" :span="24">
              <!-- XOL-602/603/604 (D14): the default slot, as three short paragraphs, because
                   `description` cannot hold paragraphs. Setting a password here still works; the
                   alert no longer says "nothing is e-mailed" without saying what is. -->
              <el-alert
                class="contact-detail__alert"
                type="info" show-icon :closable="false"
                title="Sign-in details (optional)">
                <p>Fill these in to let this contact log in. Saving does not send an e-mail, so give the Login ID and password to the contact yourself.</p>
                <p>Or fill in the Login ID only and save. Then click Send invitation on this tab. The contact gets an e-mail to choose their own password.</p>
                <p>Leave these blank if the contact does not need to log in.</p>
              </el-alert>
            </el-col>

            <!-- ───────────────────────── BLOCK 1 — LEFT ───────────────────────── -->
            <el-col :xs="24" :sm="24" :md="24" :lg="12">

              <el-form label-position="top" :inline="true">
                <el-row>
                <el-col :span="24">
                  <switch-input v-if="canEditSystemFields"
                    label="Enabled?"
                    on-text="Enabled"
                    off-text="Disabled"
                    v-model="form.enabled"
                    :disabled="disabled" />

                  <text-input
                  label="Login ID"
                   v-model="form.login"
                   placeholder="Login ID"
                  :disabled="disabled || !canEditSystemFields" />

                  <!-- Non-super on their own record: the field is read-only, so
                       name it. -->
                  <div v-if="!canEditSystemFields">
                    <small class="text-muted">This is the username you sign in with.</small>
                  </div>

                  <!-- EDIT ONLY: independent write to PUT /password/change/{id}.
                       Block-level div, so it drops to its own line under the
                       two inline-block form items above. Rendered without
                       requiring record edit mode — XOL-371 self-rotation, and
                       for a super the record's edit state is irrelevant to a
                       different endpoint's authz. -->
                  <!-- XOL-602/603/604: Change Password, the new send action and the link to the account
                       e-mails sit SIDE BY SIDE (a flex wrapper; one per line below 768px). The send
                       action is a plain block, so the inline form cannot reach it. It is rendered
                       even when the contact has no Login ID: it is then disabled and points at the
                       hint below. The hint keeps its own canManagePassword condition.

                       The link (Carlos, 2026-10-19: "Add some text next to the send reset link to
                       express the email preview for the captured email, and open the email captured
                       section") opens the Account e-mails in a slider drawer (Popup, Close-only,
                       as the request Activity log does). It used to be an inline section below
                       the form. Super only (showAccountMail); a View Only super may read it, so the
                       Popup is `allow-view-only`. The wording follows #14's mail_mode, which
                       PasswordLinkAction reports (`loaded`): capture says the e-mails are kept
                       here to preview, not delivered. -->
                  <div v-if="canManagePassword || canSendPasswordLinkSuper || showAccountMailLink">
                    <div class="contact-detail__password-actions">
                      <password-dialog v-if="canManagePassword" v-model="form" change :disabled="false" />
                      <password-link-action v-if="canSendPasswordLinkSuper" home="system"
                        :contact="initialForm" :dirty="passwordLinkDirty"
                        @sent="onPasswordLinkSent" @loaded="onPasswordLinkLoaded" />
                      <div v-if="showAccountMailLink" class="contact-detail__mail-link">
                        <popup ref="accountMailPopup"
                          :heading="accountMailCopy('list.heading')"
                          :button-text="accountMailLinkText"
                          button-style="text"
                          button-size=""
                          :button-icon="accountMailLinkIcon"
                          :show-save="false"
                          allow-view-only
                          @open="reloadAccountMail">

                          <account-mail-list ref="accountMail" :contact="initialForm" @hidden="onAccountMailHidden" />

                        </popup>
                      </div>
                    </div>
                    <div v-if="canManagePassword"><small class="text-muted">Password changes save immediately and separately from this record.</small></div>
                  </div>

                  <!-- EDIT ONLY, super, contact has no login yet: name the
                       absence. The send action points at this hint (id). -->
                  <div v-if="canEditSystemFields && !isAdding && !hasLoginAccount">
                    <small id="login-hint" class="text-muted">Give this contact a Login ID and save the record. You can then set a password or send a link here.</small>
                  </div>

                </el-col>
              </el-row>
              </el-form>

              <!-- CREATE ONLY. Deliberately a SIBLING of the el-form--inline
                   above, not a descendant (FLAG 2): that form renders
                   class="el-form--inline", and .el-form--inline .el-form-item is
                   a descendant selector that would otherwise reach through
                   PasswordForm's own :inline="false" and force its two fields
                   into mismatched inline-blocks with right-aligned labels (the
                   XOL-451 gotcha). A plain div, because its parent is already
                   the column. -->
              <div v-if="canStageCredential">

                <el-alert v-if="form.login && !form.enabled"
                  type="warning" show-icon :closable="false"
                  title="Account not enabled"
                  description="This contact will not be able to sign in until Enabled? is switched on. You can leave it off now and enable the account later." />

                <password-form v-model="form" :inline="false" />

                <!-- id matches the aria-describedby PasswordForm puts on both
                     inputs. Without it that reference dangles on the ONE surface
                     where all four credential rules are actually reachable. No
                     collision risk: canStageCredential and canManagePassword are
                     mutually exclusive, so this and the dialog's alert of the
                     same id never render together. -->
                <el-alert v-if="credentialError"
                  id="password-rules"
                  type="error" show-icon :closable="false"
                  :title="credentialError" />

              </div>

            </el-col>

            <!-- ───────────────────────── BLOCK 2 — RIGHT ────────────────────────
                 XOL-392: renders in create AND edit. `contactOrgCategory` falls
                 back to the org the contact is being created into, because the
                 contacts struct carries no orgCategory of its own; before that
                 fallback these fields silently never appeared on the create
                 form. The canEditSystemFields term inside showOrgRoleFields is
                 load-bearing twice over: every field here is in the API's
                 SUPER_ONLY_FIELDS, and on /profile `form` IS the live Vuex
                 profile() object, so without it a non-super could flip their own
                 Super / View Only switches and change the SPA's own gating for
                 the running session.
                 Each block keeps its own el-form wrapper — every child is an
                 el-form-item that reads labelPosition from an injected el-form
                 ancestor. -->
            <el-col v-if="showOrgRoleFields" :xs="24" :sm="24" :md="24" :lg="12">
              <el-form label-position="top" :inline="true">

                <select-input :list="roles" v-model="form.defaultView" label="Default View"  :disabled="disabled" />

                <el-transfer
                    v-if="!disabled"
                    v-model="formUserRoles"
                    :titles="['All Roles', 'User Roles']"
                    :data="roles"
                    @change="changeUserRoles" />

                <el-form-item  v-else label="User Roles">
                  <span  v-for="(role, i) in roles" :key="i">
                    <el-tag v-if="formUserRoles.includes(role.value)" :type="role.type">
                      {{role.label}}
                    </el-tag>&nbsp;
                  </span>
                </el-form-item>

              </el-form>
            </el-col>

            <!-- ──────────────────────── BLOCK 3 — BOTTOM ──────────────────────── -->
            <el-col v-if="showOrgRoleFields" :span="24">
              <el-form label-position="top" :inline="true">
                <el-row :gutter="5.28">

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="View Only" v-model="form.viewer" :disabled="disabled" />

                    <el-alert
                      title="View Only"
                      type="info"
                      description="View Only can't add, edit or delete anything. Only can create tickets."
                      show-icon
                      :closable="false" />
                  </el-col>

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Approver" v-model="form.approver" :disabled="disabled" />

                    <el-alert
                       title="Requests Module Only"
                       type="success"
                       description="Approver for Requests (can approve and send approval email)"
                       show-icon
                       :closable="false" />

                  </el-col>

                  <el-col :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Manager" v-model="form.manager" :disabled="disabled" />

                    <el-alert
                      title="Manager"
                      type="warning"
                      description="Manager can add, edit and delete according to role(s)"
                      show-icon
                      :closable="false" />
                  </el-col>

                  <!-- Carlos, 2026-10-19: "do not allow managers to see the super flag in the invite or
                       user create". Only a viewer who really HAS the `super` flag sees the Super
                       switch and its alert (canSeeSuperFlag), on create and on edit. -->
                  <el-col v-if="canSeeSuperFlag" :xs="24" :sm="24" :md="12" :lg="6">

                    <switch-input label="Super" v-model="form.super" :disabled="disabled" />

                    <el-alert
                      title="Super User"
                      type="error"
                      description="Super User can create users and delete anything even sites."
                      show-icon
                      :closable="false" />
                  </el-col>

                </el-row>
              </el-form>
            </el-col>

          </el-row>

      </el-tab-pane>

      <!-- XOL-392: Remarks are an administrator's notes ABOUT a contact, so
           they are suppressed on /profile. Leaving them there invited a user to
           annotate their own record in a field their managers read. -->
      <el-tab-pane name="remarks" v-if="!selfProfile && (isManager() || isSuper())" label="Remarks">

          <html-input v-model="form.remarks"
            label="Remarks"
            :show-empty-message="true"
            :disabled="disabled" />

      </el-tab-pane>

    </el-tabs>
  </el-form>

  <small v-if="!isAdding" class="text-muted">ID <b>{{form.organisation_id + '-' + form.id + '-' + form.created_at}}</b></small>
</div>

</template>


<script>

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'
import PasswordDialog from '@/components/login/PasswordDialog'
import PasswordForm from '@/components/login/PasswordForm'
import PasswordLinkAction from '@/components/login/PasswordLinkAction'
import AccountMailList from '@/components/login/AccountMailList'
import { activityText } from '@/lib/requestActivity'
import { contactRoles, normaliseOrgCategory, canHaveFinanceAlerts } from '@/lib/contactRoles'
import { passwordProblem, passwordProblemText, PASSWORD_TOO_SHORT_TEXT } from '@/lib/passwordPolicy'

// XOL-493: one string, two consumers — the inline fieldErrors.email bag AND
// the $message.error toast fired from the same branch (onSaveValidationErrors).
// Defined once so they can't drift, same reasoning this file already applies
// to credentialError (":488-491" — "so the live alert and this blocking check
// can never drift"). Rewritten rather than passed through: the server's own
// message ("The email has already been taken.") names the DB column, not the
// UI label.
var DUPLICATE_EMAIL_ERROR = 'That e-mail address is already in use by another contact.'

// XOL-493: ONE clearer, called from every path that leaves an errored state.
// Three leaks were found and patched one at a time — drawer Cancel
// (resetData), drawer reopen after a rejected save (onOpen), and inline-edit
// Cancel (onCancel). Each was the same defect in a different container, and
// each patch read as complete until the next surfaced. Route new exits through
// here rather than adding a fourth copy: the failure is silent, and it renders
// a contradiction — the inline case left "E-mail can't be cleared once an
// address has been saved" under a read-only field showing the saved address.
//
// A free function taking the vm, not a method: this file's specs call methods
// as `ContactDetail.methods.x.call(stub)` with hand-built stubs, so a method
// would force every one of them to grow a binding it does not otherwise need.
function clearFieldErrors (vm) {
  vm.fieldErrors.name = null
  vm.fieldErrors.email = null
}

export default {

  mixins: [SiteMixins],


  components: {
    PasswordDialog,
    PasswordForm,
    PasswordLinkAction,
    AccountMailList
  },



  props: {
    rowData: {
      type: Object,
      required: true
    },

    prefix: {
      type: String,
      default: 'Contact'
    },

    // XOL-392: /profile reuses this component but is NOT a contact-admin
    // screen — it is "my account". It must not offer System Details (Enabled?,
    // Login ID, privileges) or Remarks, which are things an administrator does
    // TO a contact, not things a user does to themselves. Set by UserProfile
    // only. An explicit prop rather than sniffing `prefix`, which is display
    // copy and would silently re-enable the tabs if the wording ever changed.
    selfProfile: {
      type: Boolean,
      default: false
    },

    orgId: {
      type: Number
    },

    // XOL-392: the org category to assume while ADDING, when the form itself
    // cannot supply one. `getTableStruct('contacts')` has no `orgCategory` key
    // (it is an Eloquent $appends accessor, not a column), so on create
    // `form.orgCategory` is undefined and every org-gated System Details field
    // silently failed its v-if. Ignored on an existing record, where
    // `form.orgCategory` is authoritative — see `contactOrgCategory`.
    orgCategory: {
      type: String,
      default: null
    },

    isDisabled: {
      type: Boolean,
      default: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    tableRef: {
      type: String,
      default: 'contacts-table'
    },

    adding: {
      type: Boolean,
      default: false
    },

    hidePanel: {
      type: Boolean,
      default: false
    },

    parentManaged: {
      type: Boolean,
      default: true
    }

  },



  computed: {

    // XOL-392: own record. The !isAdding guard AND the truthy login check
    // both matter: on a fresh form both profile().login and form.login could
    // compare equal via null === null. Mirrors ProfileController (authUser =
    // jwt sub).
    isSelfRecord () {
      return !this.isAdding && !!this.form && !!this.form.login &&
        this.profile().login === this.form.login
    },

    // XOL-392: mirrors ContactsController::isSuperTowerCo(). isSuper()
    // already short-circuits on !isTowerCo() (AccessMixins), but both terms
    // are kept so the mapping to the API predicate stays legible.
    canEditSystemFields () {
      return this.isSuper() && this.isTowerCo()
    },

    // XOL-392: one source of truth for "which org category is this contact in".
    // The saved record wins when it has one; on create it cannot (no such key
    // in the contacts struct), so fall back to the category passed down from
    // the org whose contacts table launched the form. Reported by Carlos:
    // "Creation does not have all system details fields ... make sure they are
    // org type gated as in update."
    contactOrgCategory () {
      if (this.form && this.form.orgCategory) {
        return this.form.orgCategory
      }

      if (!this.isAdding || !this.orgCategory) {
        return null
      }

      // The ORG row's `category` and the CONTACT's `orgCategory` are not the
      // same string. Organisation.category carries a Xolas-Client marker —
      // e.g. Xolas Sdn Bhd is literally 'Tower Company Xolas-Client' — and the
      // API strips it in Contacts::getOrgCategoryAttribute() via
      // Organisation::removeXolasClientCategory(). Comparing the raw org value
      // to 'Tower Company' would therefore be false for precisely the orgs
      // this feature is for. Mirror the server's normalisation.
      return normaliseOrgCategory(this.orgCategory)
    },

    // XOL-392: the org-gated block inside System Details — Default View, the
    // role transfer and the four privilege switches. Tower-Company-only, and
    // super-only: every field in it is in the API's SUPER_ONLY_FIELDS, so a
    // non-super would watch the server discard the lot behind a 200. The
    // canEditSystemFields term also stops the block reaching a non-super on
    // /profile, where `form` IS the live Vuex profile() object and toggling
    // Super/View Only would change the SPA's own gating for the session.
    showOrgRoleFields () {
      return this.canEditSystemFields && this.contactOrgCategory === 'Tower Company'
    },

    // XOL-674: Finance Alerts exists only where the API reads it. Tower Company
    // (contactOrgCategory: the saved record's own category, or on create the
    // launching org's, normalised) AND a finance role. form.userRole is LIVE: a
    // super changing the roles on System Details (changeUserRoles) shows or hides
    // the switch at once. On /profile `form` is profile(), which carries
    // orgCategory and userRole. A computed, never data(): both inputs are filled
    // by a bootstrap request.
    showFinanceAlerts () {
      return canHaveFinanceAlerts(this.contactOrgCategory, this.form ? this.form.userRole : null)
    },

    // XOL-674: per-instance ids (the component mounts more than once on a page: detail rows, the
    // create drawer) for the Alerts group's heading and each switch's <label for>.
    alertIds () {
      var uid = this._uid
      return {
        heading: 'contact-alerts-heading-' + uid,
        upload: 'contact-upload-alerts-' + uid,
        approval: 'contact-approval-alerts-' + uid,
        finance: 'contact-finance-alerts-' + uid
      }
    },

    // XOL-674: the three tips, from the copy table the invite dialog reads too.
    uploadAlertsHelp () {
      return activityText('contactInvite.access.uploadHint')
    },

    approvalAlertsHelp () {
      return activityText('contactInvite.access.approvalHint')
    },

    financeAlertsHelp () {
      return activityText('contactInvite.access.financeHint')
    },

    // XOL-392: the ONE branch on which stripSuperOnlyFields() is a
    // pass-through, so login/enabled survive alongside the password and the
    // created account can actually sign in.
    //
    // The extra `profile().super` term is NOT redundant with isSuper().
    // AccessMixins.isSuper() returns `super || userRole === 'super'` — the
    // second arm dates to 8196acc6 (2022-04-29) and predates the 2026 work.
    // The API's ContactsController::isSuperTowerCo() checks `super` ONLY, so a
    // Tower Company contact with `super = false, userRole = 'super'` passes
    // here and fails there. That divergence is exactly the case this guard
    // exists to stop: SUPER_ONLY_FIELDS omits `password`, so store() would
    // strip login/enabled and still hash the password, writing a credential
    // nobody can use behind a 200. Not reachable through the UI today (the
    // roles select offers no 'super' entry) but reachable from legacy rows,
    // so the write path is pinned to the server's own predicate.
    canStageCredential () {
      return this.isAdding && this.canEditSystemFields && !!this.profile().super
    },

    // Carlos, 2026-10-19: the Super switch and its alert are shown only to a viewer who HAS the
    // `super` flag, the same strict rule as canStageCredential and not isSuper(), whose legacy
    // `userRole === 'super'` arm would show it to a manager on such a row. A computed over
    // profile(): the profile is hydrated by a bootstrap request.
    canSeeSuperFlag () {
      return !!this.profile().super
    },

    // XOL-392: the account-administration surface — never on /profile, which
    // is "my account" and carries its own password button on the Contact tab.
    //
    // An earlier revision of this plan added an `|| isSelfRecord` arm so a
    // non-super would not lose self-service password change when the password
    // moved onto this tab. Giving /profile its own button removes that need
    // entirely, so the arm is gone: this is back to a plain administrator gate.
    // That also closes the privilege-leak risk it created — on /profile `form`
    // IS the live Vuex profile() object, so rendering the privileges section
    // there let a non-super flip their own `super`/`viewer` flags in-session.
    showSystemDetails () {
      return !this.selfProfile && this.canEditSystemFields
    },

    // XOL-391/XOL-392: there must be an account for a password to attach to
    // — a login id present. `enabled` is no longer required: PUT
    // /password/change/{id} does not care about it, and requiring it forced
    // a super to enable -> save -> then set the password. The switch now
    // sits in the same viewport as the password control, so the state is
    // visible where the decision is made.
    hasLoginAccount () {
      return !!(this.form && this.form.login)
    },

    // XOL-392: mirrors ChangePasswordRequest::authorize() — self-or-super.
    canManagePassword () {
      return !this.isAdding && this.hasLoginAccount &&
        (this.isSuper() || this.isSelfRecord)
    },

    // XOL-392: one source of truth for the four create-time credential
    // rules — the inline el-alert renders it live, validateCredential()
    // blocks the save with the same string. XOL-602/603/604 (D8): the password rule is
    // passwordPolicy.js's, the one every password form uses (trimmed, 8-72 characters, no Login
    // ID, confirmation) — this only adds the "no Login ID yet" rule that is specific to this
    // form. Order matters — first failure wins, and it is the policy's order. Unlike Change
    // Password, Save is NOT disabled by a short password here, so a short one is named in words.
    credentialError () {
      if (!this.canStageCredential || !this.form || !this.form.password) {
        return null
      }
      if (!this.form.login) {
        return 'Enter a Login ID above before setting a password, or clear the password.'
      }

      var problem = passwordProblem(this.form.password, this.form.c_password, this.form.login)

      if (problem === 'too_short') {
        return PASSWORD_TOO_SHORT_TEXT
      }

      return passwordProblemText(problem) || null
    },

    // XOL-602/603/604 (D1/D18): a super sends from System Details. The SAME `super` flag alone
    // as canStageCredential, not isSuper(): the API's isSuperTowerCo() checks `super` only, and
    // a legacy `super: false, userRole: 'super'` contact passes isSuper() and fails there. Not
    // on your own record (a signed-in browser ignores a link, D11; Change Password is the self
    // path) and not for View Only.
    canSendPasswordLinkSuper () {
      return !this.isAdding && !this.selfProfile && !this.isSelfRecord &&
        this.canEditSystemFields && !!this.profile().super && !this.isViewOnly()
    },

    // XOL-602/603/604 (D1/D18, R2.7): ANY Tower Co manager (the `manager` flag, whatever the
    // role), for a contact in their OWN org, not View Only, not their own record. A
    // Finance-only manager qualifies. `!showSystemDetails` is what keeps a super from seeing
    // two buttons: a super's is on System Details.
    canSendPasswordLinkManager () {
      return !this.isAdding && !this.selfProfile && !this.isSelfRecord && !this.showSystemDetails &&
        !this.isViewOnly() && this.isTowerCo() && !!this.profile().manager && this.isManager() &&
        !!this.profile().organisation_id && Number(this.form.organisation_id) === Number(this.profile().organisation_id)
    },

    // Carlos, 2026-10-19: Change Password in the manager's home. The same viewer gate as the send
    // action (any same-org Tower Co manager, not View Only, not their own record, not on a super's
    // screen), AND:
    //   - the SAVED record has a Login ID (a manager cannot add one; the send action says so), and
    //   - the target is not a super: the API answers 403 when a non-super changes a real super's
    //     password, which would be an account takeover. Send reset link stays for a super target:
    //     the link goes to the super's own e-mail. `initialForm.super` is the SAVED record, not
    //     the live form.
    // The dialog is the same one System Details uses and still calls PUT /password/change/{id}; it
    // adopts no tokens for another contact (none come back). A 403 shows saveForm's error toast.
    canChangePasswordManager () {
      return this.canSendPasswordLinkManager && !!(this.initialForm && this.initialForm.login) &&
        !(this.initialForm && this.initialForm.super)
    },

    // API review R1 (Carlos, 2026-10-19): a non-super may not change a super contact's e-mail
    // (PUT /contacts/{id} answers 403): with the e-mail changed, a reset link would take the
    // account over. `initialForm.super` is the SAVED record, `profile().super` the real flag.
    // Supers, and every non-super target, are unchanged.
    emailLocked () {
      return !this.isAdding && !this.profile().super && !!(this.initialForm && this.initialForm.super)
    },

    // The account e-mail history: System Details, so super only, on a saved contact.
    showAccountMail () {
      return !this.isAdding && this.showSystemDetails && !!this.initialForm && !!this.initialForm.id
    },

    // ... reached by the link beside the send button, until the API says it has no such list (404).
    showAccountMailLink () {
      return this.showAccountMail && !this.accountMailGone
    },

    // #14's mail_mode, from PasswordLinkAction. 'capture': the e-mails are kept on this server.
    accountMailCapture () {
      return this.passwordLinkMailMode === 'capture'
    },

    accountMailLinkText () {
      return this.accountMailCopy(this.accountMailCapture ? 'view.captured' : 'view.live')
    },

    accountMailLinkIcon () {
      return this.accountMailCapture ? 'fa fa-inbox' : 'fa fa-envelope-open'
    },

    // The send action goes to the SAVED e-mail address and acts on the saved Login ID and
    // Enabled? state. While the form differs from the record, it asks to save first.
    passwordLinkDirty () {
      var form = this.form || {}
      var saved = this.initialForm || {}
      var text = function (v) { return v === null || v === undefined ? '' : String(v).trim() }

      return text(form.email) !== text(saved.email) ||
        text(form.login) !== text(saved.login) ||
        !!form.enabled !== !!saved.enabled
    },

    // XOL-493 (Q1): required on create; on edit, blocked only if
    // initialForm.email held a value — a legacy blank stays editable so the
    // 7 contacts with a blank e-mail (all 7 with a live login) don't get
    // locked out of every unrelated edit. One computed so the asterisk (the
    // E-mail el-form-item's :required) and the blocking check
    // (validateContactFields, which reads this rather than re-deriving it)
    // can never drift — same reasoning as credentialError above.
    emailRequired () {
      return this.isAdding || !!(this.initialForm && this.initialForm.email)
    }

  },


  methods: {

    accountMailCopy (key) {
      return activityText('accountMail.' + key)
    },

    // The send action read #14: keep the mail mode for the link's wording (null = unknown, so live).
    onPasswordLinkLoaded (info) {
      this.passwordLinkMailMode = info ? info.mailMode : null
    },

    // The send action queued an e-mail: the history shows it (as Queued) straight away. The list
    // exists once the drawer has been opened; if it has not, the first open reads it.
    onPasswordLinkSent () {
      this.reloadAccountMail()
    },

    // Popup emits `open` before it shows the drawer, so on the first open there is no list yet
    // (it is created, and reads itself, when the drawer renders); on every later open this
    // reads it again, so it is never stale.
    reloadAccountMail () {
      if (this.$refs.accountMail && typeof this.$refs.accountMail.refresh === 'function') {
        this.$refs.accountMail.refresh()
      }
    },

    // A 404 from the list (an older API): no link, and the drawer goes with it.
    onAccountMailHidden () {
      this.accountMailGone = true
    },

    changeUserRoles (roles) {
      let userRole = ''

      if (roles.length > 0) {

         for (let s in roles) {
             userRole += (s > 0 ? '|' + roles[s] : roles[s])
           }

         } else {
           userRole = this.formUserRoles[0]
         }

     this.form.userRole = userRole
    },



    resetData () {
      this.form.id = null
      this.form.name = null
      this.form.shortName = null
      this.form.jobTitle = null
      this.form.phone = null
      this.form.email = null
      this.form.enabled = false
      this.form.login = null
      this.form.sendUploadAlerts = false
      this.form.sendApprovalAlerts = false
      // XOL-674: the key always exists on a create form (data() / onAdd() default it), so this is reactive.
      this.form.sendFinanceAlerts = false
      this.form.remarks = null

      // XOL-490/493: fieldErrors is written by validateContactFields() (a
      // blocked Save) and onSaveValidationErrors() (a 422 from the server) —
      // both fire outside the tab machinery that owns every other field
      // above, so nothing else ever cleared them. The drawer is never
      // destroyed (see the password comment below), so a Name/E-mail error
      // left over from one contact would otherwise sit under the next
      // contact's blank field — worst case the duplicate-e-mail message,
      // because Popup.saveModal() closes the drawer and emits 'save' but
      // never 'close' on that path, so this is the only point that reaches it.
      clearFieldErrors(this)

      // XOL-392: the org-gated role fields now RENDER on create, so they have
      // to be reset like everything else. The drawer is never destroyed, so
      // without this a super who ticked Super/Manager on one contact and
      // cancelled would silently carry those privileges into the next New
      // Contact — the worst possible field to leave sticky.
      this.form.defaultView = null
      this.form.userRole = null
      this.form.viewer = false
      this.form.approver = false
      this.form.manager = false
      this.form.super = false
      this.formUserRoles = []

      // XOL-392: the drawer is not destroyed on close — Popup.closeModal()
      // keeps the slot mounted — so a plaintext password typed here would
      // otherwise survive into the next New Contact. Assign field-by-field;
      // do NOT replace this.form wholesale (the struct has no c_password, so
      // swapping the object would hand PasswordForm a non-reactive property —
      // the exact bug XOL-391 fixed with $set).
      this.form.password = null
      this.form.c_password = null
    },



    success (response) {
      // XOL-392: the drawer is not destroyed on save either — Popup.saveModal()
      // emits 'save' but never 'close' and el-drawer keeps the slot mounted —
      // so a plaintext password typed here would otherwise survive into the
      // next New Contact. ContactAction builds contactData once in data() and
      // reuses the same object, so this is the only clear-point on the save
      // path.
      if (this.isAdding) {
        this.form.password = null
        this.form.c_password = null
      }

      this.initialForm = { ...this.form }
      this.$events.emit(this.tableRef + '-refresh-table')
    },



    onCancel () {
      this.disabled = true
      if (this.parentManaged) this.editModeState = false

      // Blur the active element if it belongs to this form before resetting.
      // Rationale: after a blocked tab-switch, the browser can latch an
      // invalid required <input> into a visual state where a subsequent
      // programmatic value assignment (via Element UI's nativeInputValue
      // watcher) is not repainted. Releasing focus clears the latch.
      // See .context/plans/contact-detail-stale-input-plan.md.
      const formEl = this.$refs.contactDetailsForm && this.$refs.contactDetailsForm.$el
      const active = typeof document !== 'undefined' ? document.activeElement : null
      if (formEl && active && formEl.contains(active) && typeof active.blur === 'function') {
        active.blur()
      }

      // XOL-493: Cancel restores the form, so any validation error raised
      // against the discarded edit is stale the moment it lands. Left in
      // place it renders a contradiction: the read-only field shows the saved
      // address while the message says the address cannot be cleared.
      clearFieldErrors(this)

      this.$nextTick(() => {
        this.form = { ...this.initialForm }
      })
    },



    onEdit () {
      this.disabled = false
      if (this.parentManaged) this.editModeState = true
    },



    onAdd () {
      this.disabled = false
      this.isAdding = true

      var id = this.form.organisation_id
      var struct = this.getTableStruct('contacts')
      // XOL-674: the structure comes from /api/system/config, which is cached API-side and has no
      // sendFinanceAlerts until `system:table-cache` runs. Default the key BEFORE the object is
      // assigned, so Vue makes it reactive (SwitchInput keeps its own copy of `value`, so a
      // non-reactive reset would leave the switch on while the form holds false).
      if (!('sendFinanceAlerts' in struct)) {
        struct.sendFinanceAlerts = false
      }
      this.form = struct
      this.form.organisation_id = id

      // XOL-490/493: same leak as resetData() above — swapping in a fresh
      // form does not touch the separate fieldErrors bag.
      clearFieldErrors(this)

      if (this.parentManaged) this.editModeState = true
    },


    // Bound to el-tabs :before-leave — the original validateForm() body,
    // verbatim. Returning false here BLOCKS the tab switch (Element's
    // tabs.vue), so it must never contain a rule the user can only fix on
    // another tab — see validateCredential() below.
    canLeaveTab () {
      const form = this.$refs.contactDetailsForm.$el
      if (!form.checkValidity()) {
        form.reportValidity()
        return false
      }
      return true
    },

    // XOL-493: Name/E-mail are on the Contact tab, and their rules go here —
    // never in canLeaveTab() above, whose own comment forbids a rule the user
    // can only fix on another tab. Pure function of `this`: reads
    // form/initialForm/isAdding/emailRequired, writes fieldErrors, returns a
    // Boolean. No $nextTick, no watcher — deliberately tick-free so the
    // mount-free spec can observe it directly; focus handling lives in the
    // separate focusFirstFieldError() below.
    //
    // Validates the TRIMMED value without mutating `form` — whitespace-only
    // is "required", not "too short". Sets every applicable error in one
    // pass (no early return, XOL-487 D4's stacking decision) and clears both
    // keys at the top of every run.
    validateContactFields () {
      clearFieldErrors(this)

      var name = (this.form && this.form.name != null) ? String(this.form.name).trim() : ''
      var email = (this.form && this.form.email != null) ? String(this.form.email).trim() : ''

      if (!name) {
        this.fieldErrors.name = 'Name is required.'
      } else if (name.length < 3) {
        this.fieldErrors.name = 'Name must be at least 3 characters.'
      } else if (name.length > 120) {
        this.fieldErrors.name = 'Name must be 120 characters or fewer.'
      }

      if (!email) {
        if (this.emailRequired) {
          // Distinct from "required": this is an edit where initialForm.email
          // held a value and the user has since cleared it — a removal, not a
          // failure to supply (copy #6 vs #4).
          this.fieldErrors.email = this.isAdding
            ? 'E-mail is required.'
            : 'E-mail can\'t be cleared once an address has been saved. Enter a new address, or Cancel to restore it.'
        }
        // else: a legacy blank e-mail on edit (initialForm.email was never
        // set) stays editable — the whole point of the Q1 rule (B2).
      } else if (email.length < 3) {
        // Effectively unreachable — native type="email" rejects anything
        // shorter than a@b.c — kept so the rule table is complete.
        this.fieldErrors.email = 'E-mail must be at least 3 characters.'
      } else if (email.length > 80) {
        // 80, matching contacts.email varchar(80). XOL-494 tightened the API's
        // rule to max:80 as well (ContactRules::EMAIL_MAX), so the two now
        // agree — the "API rule is looser" follow-up this used to name has
        // landed.
        this.fieldErrors.email = 'E-mail must be 80 characters or fewer.'
      }

      return !this.fieldErrors.name && !this.fieldErrors.email
    },

    // XOL-493: the involuntary tab jump (validateForm() below) must not also
    // steal focus from a native reportValidity() bubble — canLeaveTab() can
    // still fail on some OTHER native-invalid control, and stealing focus
    // there would drop the bubble's message. Self-guarded on fieldErrors
    // having an entry, rather than relying on the caller, so it is safe to
    // call unconditionally and testable in isolation (a stubbed $nextTick and
    // a $refs stub carrying jest.fn() focus — mount-free, per this file's
    // spec). Deliberately NOT inside validateContactFields(), which stays
    // pure and tick-free.
    focusFirstFieldError () {
      if (!this.fieldErrors || (!this.fieldErrors.name && !this.fieldErrors.email)) {
        return
      }
      var self = this
      this.$nextTick(function () {
        var ref = self.fieldErrors.name ? 'nameInput' : 'emailInput'
        var input = self.$refs[ref]
        if (input && typeof input.focus === 'function') {
          input.focus()
        }
      })
    },

    // XOL-493: validateContactFields() must run LEFTMOST and unconditionally
    // — canLeaveTab() calls reportValidity(), which pops a native bubble, and
    // the inline field errors must be populated before that happens. Storing
    // the result also drives the tab-jump below, guarded to the case where a
    // FIELD error (not a validateCredential() failure) is why the save was
    // blocked — a credentialError alone must not yank the user off System
    // Details, where its password fields live.
    validateForm () {
      var fieldsOk = this.validateContactFields()
      if (!fieldsOk) {
        this.$message({ type: 'error', message: 'Fix the highlighted errors before saving.' })
        this.currentTab = 'contact'
        this.focusFirstFieldError()
      }
      return fieldsOk && this.canLeaveTab() && this.validateCredential()
    },

    // XOL-392: the inline create-time password fields sit inside the outer
    // <el-form>, but pairing and length rules are not expressible natively,
    // and reportValidity() cannot focus a control on a v-show-hidden tab
    // pane (the Contact tab can be the active one when Save is clicked).
    // Rules mirror Register.vue and credentialError above — kept as a single
    // computed so the live alert and this blocking check can never drift.
    validateCredential () {
      var problem = this.credentialError
      if (!problem) {
        return true
      }
      this.$message({ type: 'error', message: problem })
      return false
    },

    // XOL-392: fires when the /contacts save 422s with a login- or
    // email-specific validation error (renamed from onDuplicateLogin in
    // XOL-493 to cover both). The generic PostMixins.send() message already
    // names the DB column ("The login/email has already been taken.")
    // without saying which tab to fix it on — this adds a targeted message,
    // and for e-mail also writes the inline fieldErrors bag and returns the
    // user to the Contact tab. API item A5 (ContactsRequest::attributes())
    // retires the login half once it lands.
    //
    // XOL-493 residual: Q2 (converting ContactAction to keepOpenUntilSaved)
    // is deferred, so on the /contacts create path the drawer has ALREADY
    // closed by the time this runs — fieldErrors.email is written into a
    // dismissed component, and the toast below is the user's only feedback.
    // The inline half is forward-compatible and becomes visible once Q2
    // lands.
    //
    // XOL-509: an earlier revision said `unique:contacts,email` is create-only
    // and therefore "unreachable on either edit path". That was true until
    // XOL-494 (API 827265ec) bound ContactRules to the update paths too, and
    // it was worse than an ordinary stale comment — it was the stated reason
    // the email case went unhandled, so a reader took it as licence to leave
    // the gap. Since XOL-494, PUT /api/contacts/{contact} and PUT /api/profile
    // both 422 on required / min:3 / max:80 / unique for email, and on
    // required / min:3 / max:120 for name. See app/Http/Requests/ContactRules.php.
    //
    // Note uniqueness is CASE-INSENSITIVE on live and demo
    // (contacts.email is latin1_swedish_ci; only the test DB is utf8mb3_bin),
    // so changing just the capitalisation of an address another contact holds
    // returns 422. That is precisely the failure a user cannot diagnose from a
    // toast alone, which is why these render inline against the field.
    onSaveValidationErrors (errors) {
      // XOL-509: re-enable the form before writing any inline error.
      //
      // onSave() sets `disabled = true` (and clears editModeState) as it
      // dispatches, and nothing on the failure path put it back — so an
      // inline error rendered under Name or E-mail sat against a GREYED-OUT
      // input the user could not act on without hunting for Edit again. The
      // client-validation path a few lines up already does this
      // (`if (this.parentManaged) this.editModeState = true`); the server-422
      // path did not, which only became visible once this ticket made 422s
      // reach here at all.
      var handled = !!(errors && (errors.login || errors.email || errors.name))
      if (handled) {
        this.disabled = false
        if (this.parentManaged) this.editModeState = true
      }

      if (errors && errors.login) {
        this.$message({
          type: 'error',
          message: 'That Login ID is already taken. Choose a different one on the System Details tab.'
        })
      }

      // XOL-509: before XOL-494 the only reachable email rule was `unique`, so
      // a fixed "already in use" string was always right. The update paths now
      // also enforce required / min:3 / max:80, and labelling a length failure
      // "already in use" would send the user hunting a conflict that does not
      // exist.
      //
      // So: keep the approved SPA wording for the duplicate case — it reads
      // better than Laravel's default and explains that ANOTHER contact holds
      // the address — and defer to the server's message for every other rule.
      // The match is deliberately loose and the fallback is the server text, so
      // a reworded API message degrades to something still accurate rather
      // than to a wrong label.
      if (errors && errors.email) {
        // Three arms, deliberately. A matched duplicate gets the approved SPA
        // wording; any other rule gets the server's own message; and a MISSING
        // message gets a neutral prompt rather than the most specific claim
        // available. An earlier revision defaulted the empty case to
        // DUPLICATE_EMAIL_ERROR, which asserted "already in use by another
        // contact" on no evidence at all — the same failure that cost XOL-497
        // four review rounds.
        var raw = (Array.isArray(errors.email) && errors.email[0]) ? errors.email[0] : ''
        var emailMsg = raw
          ? (/already been taken/i.test(raw) ? DUPLICATE_EMAIL_ERROR : raw)
          : 'Check the e-mail address and try again.'
        this.fieldErrors.email = emailMsg
        this.$message({ type: 'error', message: emailMsg })
        this.currentTab = 'contact'
      }

      // XOL-509: name is rejectable on both write paths since XOL-494
      // (required, min:3). It was unhandled because no edit path could 422.
      if (errors && errors.name) {
        var nameMsg = (Array.isArray(errors.name) && errors.name[0])
          ? errors.name[0]
          : 'Enter a valid name.'
        this.fieldErrors.name = nameMsg
        this.$message({ type: 'error', message: nameMsg })
        this.currentTab = 'contact'
      }
    },

    onSave (orgId) {
      if (!this.validateForm()) {
        if (this.parentManaged) this.editModeState = true
        return
      }

      if (orgId && this.isAdding) {
        this.org_id = orgId
        this.form.organisation_id = this.org_id
      }

      if (this.form.sendUploadAlerts || this.form.sendApprovalAlerts) {

        if (this.isNull(this.form.sendUploadAlerts)) {
          this.form.sendUploadAlerts = false
        }

        if (this.isNull(this.form.sendApprovalAlerts)) {
          this.form.sendApprovalAlerts = false
        }

        // XOL-674: sendFinanceAlerts is deliberately NOT normalised here. It is NOT NULL DEFAULT 0
        // server-side, and on /profile an `undefined` (a profile saved at login before the API
        // deploy) turned into false would undo a tick an admin set since.

      }

      this.disabled = this.isAdding ? false : true
      if (this.parentManaged) this.editModeState = false

      // XOL-371: a contact editing their OWN record saves to the self-service
      // profile endpoint (server derives the user from the JWT, allow-list
      // fields only). Managing another contact, or adding one, keeps the
      // existing /contacts resource save.
      if (!this.isAdding && this.profile().login === this.form.login) {
        this.saveSelfProfile()
      } else {
        this.saveForm(this, '/contacts', this.form.id, this.contactPayload(), this.form.name, this.success, this.onSaveValidationErrors)
      }
    },


    // XOL-391: the API validates `login` with `sometimes|unique:contacts,login`.
    // `sometimes` only skips a key that is ABSENT — sending `login: null` still
    // runs the unique check, and Laravel turns a null value into
    // `WHERE login IS NULL`, which matches every login-less contact already in
    // the table. Creating a contact without a login therefore failed with a
    // bogus "The login has already been taken." (resetData() sets login to
    // null, so this hit every new contact that wasn't given a login id).
    // Omit the key entirely when there is no login — exactly what `sometimes`
    // is for — so the column keeps its NULL default instead of an empty string.
    // Only the create path validates login this way (update() takes a plain
    // Request, not ContactsRequest), but the payload is built the same way for
    // both so an empty login never means "clear this field to ''".
    contactPayload () {
      var payload = { ...this.form }

      if (payload.login === null || payload.login === undefined || payload.login === '') {
        delete payload.login
      }

      // XOL-392: `password` travels with POST /contacts ONLY when a super is
      // creating. On every other path the /contacts resource carries no
      // credential: PUT /contacts/{id} would hash it (ContactsController
      // update()) with no min:8 rule and a WIDER authz than /password/change
      // (isSameOrgManager || isSelf), and for a non-super caller store()
      // strips login/enabled while KEEPING password — an orphan credential on
      // a contact that cannot sign in. normalizePassword() also drops
      // empty/null values server-side, so the !payload.password arm is
      // belt-and-braces.
      if (!this.canStageCredential || !payload.password) {
        delete payload.password
      }

      // `c_password` is the confirm-field companion the password components
      // seed onto the shared form — not a contacts column, never sendable.
      delete payload.c_password

      // XOL-674: hidden means the API ignores it (not a Tower Company finance
      // contact). Store false so a stale tick cannot start mailing the day the role
      // gains finance without anyone seeing the switch on (plan Q1).
      if (!this.showFinanceAlerts) {
        payload.sendFinanceAlerts = false
      }

      return payload
    },


    // XOL-371: PUT /api/profile — self-service save of the logged-in
    // contact's own record. Own record only (server: contact.id === jwt.sub);
    // send ONLY the self-editable fields (no role/system fields). Mirrors
    // saveForm()'s use of PostMixins' send() for success/error handling.
    // Paired endpoint: ProfileController@update in xolas-tms-api (XOL-371).
    //
    // XOL-392: `password` is deliberately NOT sent here, even though it is in
    // ProfileController::SELF_EDITABLE server-side — this is an SPA-side
    // decision, not a server constraint. No UI intends to set a password via
    // PUT /api/profile: the password dialog performs its own, independent
    // PUT /password/change/{id}. On /profile `form` IS the live Vuex
    // profile() object (assigned by reference), and PasswordDialog's
    // formChange() replaces its OWN form reference on fetch — so it does not
    // reliably clear ContactDetail's copy. Not sending it here is the only
    // reliable fix for a cancelled/dismissed dialog password otherwise riding
    // this save.
    saveSelfProfile () {
      var payload = {
        name: this.form.name,
        shortName: this.form.shortName,
        jobTitle: this.form.jobTitle,
        phone: this.form.phone,
        email: this.form.email,
        sendUploadAlerts: this.form.sendUploadAlerts,
        sendApprovalAlerts: this.form.sendApprovalAlerts
      }

      // XOL-674: only when the switch is shown, and the RAW value like the two above (never !!): a
      // profile saved before the API deploy has no key (undefined), and JSON drops it, so the stored
      // value is left alone. When hidden the key is omitted, not sent as false: a user cannot change
      // their own org or role, and profile() is a login-time snapshot, so a false here could undo a
      // tick the user never saw.
      if (this.showFinanceAlerts) {
        payload.sendFinanceAlerts = this.form.sendFinanceAlerts
      }

      // allowViewOnly=true (last arg): /profile is the one self-service surface a
      // view-only user may write; the API keeps PUT /api/profile exempt too.
      // XOL-509: the 6th argument is send()'s failCallback and was `null`, so a
      // 422 from PUT /api/profile never reached onSaveValidationErrors — the
      // user got a popup carrying the server text with no field named and no
      // inline error. send()'s `data.errors` branch invokes failCallback
      // UNGATED (unlike the network-error branches, which require
      // notifyFailureCallback), so wiring the handler is the whole fix; the
      // trailing flags are unchanged.
      this.send(this, this.getEndpoint('profile', null, false), payload, this.form.name, this.success, this.onSaveValidationErrors, axios.put, true, 'Save', null, false, true)
    },


    tabChanged (pane) {
      this.currentTab = pane.name
    },



    onClose () {
      this.resetData()
    },

    // XOL-493: clears validation state on every drawer open. resetData() is
    // reached only via onClose(), i.e. Cancel — but Popup.saveModal() closes
    // the drawer without emitting `close`, so a REJECTED save leaves
    // fieldErrors populated on a still-mounted component. Without this, the
    // next New Contact opens showing that error under a blank field, and in
    // the duplicate-e-mail case it belongs to a different contact entirely.
    onOpen () {
      clearFieldErrors(this)
    }

  },



  data () {
    const form = this.adding ? this.getTableStruct('contacts') : this.rowData

    // XOL-674: see onAdd(). Set before the object is returned, so Vue makes the key reactive.
    if (this.adding && !('sendFinanceAlerts' in form)) {
      form.sendFinanceAlerts = false
    }

    let formUserRoles = []
    if (form.userRole) {
      formUserRoles = form.userRole.trim().split('|')
    }

    return {
      disabled: this.isBoolean(this.isDisabled) ? this.isDisabled : true,
      emailDisabled: !(this.adding && this.isManager() || this.profile().login === this.rowData.login),
      isAdding: this.adding,
      // XOL-602/603/604: #14's mail_mode, reported by PasswordLinkAction (null until it answers,
      // and when it fails or is not shown). It words the link to the account e-mails. And: the list
      // answered 404, so there is no history to link to.
      passwordLinkMailMode: null,
      accountMailGone: false,
      formUserRoles: formUserRoles,
      roles: contactRoles(),
      org_id: (this.rowData.organisation_id ? this.rowData.organisation_id : this.orgId),
      form: form,
      initialForm: { ...form },
      editModeState: this.editMode,
      currentTab: 'contact',
      // XOL-493: both keys declared up front, no $set — plain object,
      // written by validateContactFields()/onSaveValidationErrors(), read by
      // the Name/E-mail el-form-items' :error bindings.
      fieldErrors: { name: null, email: null }
    }
  },

  created () {

    if (this.isAdding) {
      this.disabled = false
      delete this.form.id
    }

    if (!this.form.organisation_id) {
      this.form.organisation_id = this.org_id
    }

  }

}

</script>

<!-- XOL-490/493: Name/E-mail sit as full-width block children of a 96.3%-wide
     drawer with a 90px label gutter — narrower than the inline-flow fields
     XOL-487 styled with min-width:240px, and too narrow to hold Element's
     absolutely-positioned .el-form-item__error on one line without it
     overlaying the Upload Alerts checkboxes below. Force it in-flow instead;
     the ~16px reflow when an error appears is accepted (reflow beats overlap,
     and it happens on a pane the user is being moved to anyway). Plain CSS,
     matching this file — not scss. -->
<style scoped>
/* XOL-602/603/604: the manager's send action, a block after the Alerts section. */
.contact-detail__password-link { margin: 4px 0 14px; }

/* XOL-674: Element draws no focus state on an el-switch; the checkbox it replaces did. The repo's
   existing ring (EmailSetup, step3), for keyboard focus only. */
.contact-detail__alerts ::v-deep .el-switch__input:focus-visible ~ .el-switch__core { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #409EFF; }

/* Change Password and Send invitation / Send reset link, side by side; one per line on a phone. */
.contact-detail__password-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-start; margin: 2px 0 6px; }
@media (max-width: 767px) {
  .contact-detail__password-actions { flex-direction: column; }
}

/* The one-line reason under a locked field: #606266 on white (.text-muted fails contrast). */
.contact-detail__hint { display: block; margin-top: 4px; line-height: 1.4; color: #606266; }

/* The create alert's three paragraphs (Element's description is one paragraph). */
.contact-detail__alert p { margin: 0 0 4px; }

.contact-detail__validated ::v-deep .el-form-item__error {
  position: static;
  padding-top: 4px;
  line-height: 1.4;
}
</style>
